<template>
    <Portal to="modal">
        <ModalContainer key="registerModal">
            <template #head>
                <RegisterHead
                    v-if="!isShowingPaymentSuccess"
                    :current-step="currentStep"
                    :selected-exam="selectedExam"
                    :is-free-exam="!!selectedExam && selectedExam.isFree"
                    @back="goBack"
                />
            </template>
            <div
                class="register"
                :class="{
                    'register--payment-success': isShowingPaymentSuccess,
                }"
            >
                <template v-if="!isLoading">
                    <Banner
                        v-if="validReferral && currentStep <= 4 && referralMetadataBannerMessage"
                        class="register__banner"
                        :class="{
                            'register__banner--referral-metadata-banner-message': referralMetadataBannerMessage,
                        }"
                    >
                        <template #bannerText>
                            {{ referralMetadataBannerMessage }}
                        </template>
                    </Banner>
                    <Banner
                        v-else-if="validReferral && currentStep <= 4"
                        class="register__banner"
                    >
                        <template #bannerText>
                            <template
                                v-if="'name' in validReferral && validReferral.name">
                                {{ validReferral.name }}
                            </template>
                            <template v-else-if="'objectId' in validReferral">
                                Someone sent you 20% off your first subscription with Pocket Prep. Sweet!
                            </template>
                        </template>
                    </Banner>
                    <Banner
                        v-else-if="validGift && currentStep <= 4"
                        class="register__gift-banner"
                    >
                        <template #bannerImage>
                            <img
                                class="register__banner-gift-emoji"
                                src="@/assets/gift/gift-emoji.png"
                                alt=""
                            >
                        </template>
                        <template #bannerText>
                            You’re redeeming {{ giftTierDuration }} of Premium access!
                        </template>
                    </Banner>
                    <Errors
                        v-else-if="!validReferral && referralError && currentStep <= 2"
                        :errors="[ referralError ]"
                        class="register__error-banner"
                    />
                    <Errors
                        v-else-if="!validGift && giftError && currentStep <= 2"
                        :errors="[ giftError ]"
                        class="register__error-banner"
                    />
                    <div
                        v-if="currentStep > 0 && currentStep <= 4 && !isShowingPaymentSuccess"
                        class="register__step"
                        :class="{
                            'register__step--referral': validReferral,
                            'register__step--gift': validGift
                        }"
                        aria-live="polite"
                    >
                        <!-- Allow empty state while loading between steps -->
                        <template v-if="isLoadingStep" />
                        <template
                            v-else-if="currentStep === 2 && !!selectedExam"
                        >
                            The {{ selectedExam.nativeAppName }} — Excellent!
                        </template>
                        <template v-else-if="showTryAQuestion">
                            <template
                                v-if="tryAQuestionScreenName === 'pass_with_confidence'"
                            >
                                {{ passWithConfidenceSubHeader }}
                            </template>
                            <template
                                v-else-if="tryAQuestionScreenName === 'try_a_question'"
                            >
                                Bite-sized learning awaits
                            </template>
                            <template
                                v-else-if="tryAQuestionScreenName === 'one_question_down'"
                            >
                                And just like that
                            </template>
                        </template>
                        <template v-else>
                            Step {{ Math.max(currentStep - 1, 1) }} of 3
                        </template>
                    </div>
                    <h1
                        v-if="!isShowingPaymentSuccess"
                        class="register__title"
                        :class="{
                            'register__title--payment': currentStep === 5,
                            'register__title--marketing-campaign-first-time':
                                isMarketingCampaign
                                && currentStep === 2
                        }"
                        tabindex="-1"
                        role="heading"
                    >
                        <!-- Allow empty state while loading between steps -->
                        <template v-if="isLoadingStep" />
                        <template v-else-if="currentStep === 1 && !selectedExam">
                            {{ showMarketingExamScreenUpdates ?
                                'Let\'s prep! Choose your exam to get started.' :
                                'Let\'s prep! What exam are you studying for?'
                            }}
                        </template>
                        <template v-else-if="currentStep === 1 && !!selectedExam">
                            Choose Exam Version
                        </template>
                        <template v-else-if="currentStep === 2 && !!selectedExam">
                            {{ isMarketingCampaign ?
                                `Have you taken the ${ selectedExam.nativeAppName } before?` :
                                'Have you taken this exam before?'
                            }}
                        </template>
                        <template v-else-if="currentStep === 3">
                            <template v-if="showTryAQuestion && tryAQuestionScreenName === 'pass_with_confidence'">
                                Ready to pass with confidence?
                            </template>
                            <template v-else-if="showTryAQuestion && tryAQuestionScreenName === 'try_a_question'">
                                Try a question?
                            </template>
                            <template v-else-if="showTryAQuestion && tryAQuestionScreenName === 'one_question_down'">
                                1 question down!
                            </template>
                            <template v-else>
                                Account Details
                            </template>
                        </template>
                        <template v-else-if="currentStep === 4">
                            <!-- Perceived Value Experiment -->
                            {{ perceivedValueVariantValue === 'header-treatment'
                                ? 'Build Your Future'
                                : 'Choose Your Plan'
                            }}
                        </template>
                        <template v-else-if="currentStep === 5">
                            Add Payment & Review
                        </template>
                    </h1>
                    <div
                        v-if="!isShowingPaymentSuccess && currentStep === 5"
                        class="register__subtitle"
                        :class="{
                            'register__subtitle--error': paymentError,
                        }"
                    >
                        <template v-if="paymentError">
                            {{ paymentError }}
                        </template>
                        <template v-else>
                            Last step! You can cancel anytime.
                        </template>
                    </div>
                    <!-- Allow empty state while loading between steps -->
                    <template v-if="isLoadingStep" />
                    <Onboarding
                        v-else-if="currentStep === 0"
                        :valid-referral="!!validReferral"
                        :valid-gift="!!validGift"
                        :is-marketing-campaign="isMarketingCampaign"
                        @next="moveToChooseExam"
                    />
                    <ChooseExam
                        v-else-if="currentStep === 1 && !selectedExam"
                        class="register__choose-exam"
                        :hide-exam-search-bar="showMarketingExamScreenUpdates"
                        @select="selectExam"
                        @selectBundle="selectBundle"
                        @search="search"
                    />
                    <SelectVersion
                        v-else-if="currentStep === 1 && !!selectedExam && examVersions.length > 1"
                        :current-exam="selectedExam"
                        :exam-versions="examVersions"
                        :bundle="selectedExam.bundle"
                        @next="moveToConfirmExam"
                        @cancel="backToExamSearch"
                        @selectExamVersion="selectExamVersion"
                    />
                    <FirstTimeExamAsk
                        v-else-if="currentStep === 2"
                        :has-taken-exam="hasTakenExam"
                        :is-marketing-campaign="isMarketingCampaign"
                        :showing-try-a-question-next="showingTryAQuestionNext"
                        @updateHasTakenExam="updateHasTakenExam"
                        @submit="submitFirstTimeExamAsk"
                        @goBack="goBack"
                    />
                    <PassWithConfidence
                        v-else-if="showTryAQuestion && tryAQuestionScreenName === 'pass_with_confidence'"
                        @tryAQuestion="tryAQuestion"
                    />
                    <TryAQuestion
                        v-else-if="showTryAQuestion && tryAQuestionScreenName === 'try_a_question'"
                        @seeStats="seeStats"
                    />
                    <OneQuestionDown
                        v-else-if="showTryAQuestion && tryAQuestionScreenName === 'one_question_down'"
                        @createAccount="createAccount"
                    />
                    <AccountDetails
                        v-else-if="currentStep === 3 && selectedExam"
                        :exam="selectedExam"
                        :saved-password="password"
                        :exam-versions="examVersions"
                        :valid-referral="!!validReferral"
                        :valid-gift="!!validGift"
                        :activation-error="premiumActivationError"
                        :is-activating-gift="isLoadingPremiumActivation"
                        :first-name-query="firstNameQuery"
                        :last-name-query="lastNameQuery"
                        :email-query="emailQuery"
                        @submit="submitAccountDetails"
                        @clearActivationError="premiumActivationError = ''"
                    />
                    <ChoosePlan
                        v-else-if="currentStep === 4 && selectedExam"
                        :exam="selectedExam"
                        :bundle="selectedExam.bundle"
                        :saved-selected-plan-id="selectedPlanId"
                        :valid-referral="!!validReferral"
                        :referral-error="referralError"
                        :exam-has-level-up-mode="examHasLevelUpMode"
                        :referral-metadata-preselected-plan="referralMetadataPreselectedPlan"
                        :perceived-value-variant="perceivedValueVariantValue"
                        @confirmPlan="confirmPlan"
                    />
                    <PaymentFields
                        v-else-if="
                            currentStep === 5
                                && selectedPlanId
                                && selectedPlanId !== 'free'
                                && selectedExam
                                && !isShowingPaymentSuccess
                        "
                        :key="paymentFieldsRefreshTime"
                        mode="subscription"
                        :price-id="selectedPlanId"
                        :referral-id="referralId"
                        @checkoutCompleted="checkoutCompleted"
                        @error="handlePaymentFieldsError"
                    />
                    <MountainSuccess
                        v-else-if="isShowingPaymentSuccess"
                        class="register__payment-success"
                        :is-loading="isLoadingPremiumActivation"
                        :errorMessage="premiumActivationError"
                        @close="registrationCompleted"
                    />
                </template>
                <div
                    class="register__financial-support"
                    v-if="currentStep === 4 && selectedExam"
                >
                    <div class="register__financial-support-message">
                        Need help paying for Premium? Learn about our
                        <PocketLink
                            :href="`https://www.pocketprep.com/pocket-prep-financial-support-program?${
                                buildUTMParamString({
                                    utm_campaign: 'app_settings',
                                    utm_content: 'financial_support_program',
                                })
                            }`"
                            target="_blank"
                            class="register__financial-support-link"
                            aria-label="Financial Support Program. Open help center article in browser view."
                            @mousedown.prevent
                        >
                            Financial Support Program
                            <Icon type="externalLink" />
                        </PocketLink>
                    </div>
                </div>
            </div>
        </ModalContainer>
    </Portal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { Study } from '@pocketprep/types'
import type { ClientBundle, ClientExam } from '@/store/bundle/state'
import UIKit from '@pocketprep/ui-kit'
import * as Sentry from '@sentry/browser'
import Onboarding from '@/components/Register/Onboarding.vue'
import ChooseExam, { type TBundleExam } from '@/components/Register/ChooseExam.vue'
import SelectVersion from '@/components/Register/SelectVersion.vue'
import AccountDetails from '@/components/Register/AccountDetails.vue'
import ChoosePlan from '@/components/Register/ChoosePlan.vue'
import RegisterHead from '@/components/Register/RegisterHead.vue'
import { stripeModule } from '@/store/stripe/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { bundleModule } from '@/store/bundle/module'
import { userModule } from '@/store/user/module'
import { referralModule } from '@/store/referral/module'
import { analyticsModule } from '@/store/analytics/module'
import { subscriptionModule } from '@/store/subscription/module'
import { buildUTMParamString } from '@/store/utils'
import PaymentFields from '@/components/PaymentFields.vue'
import MountainSuccess from '@/components/MountainSuccess.vue'
import FirstTimeExamAsk from '@/components/Register/FirstTimeExamAsk.vue'
import { upgradeModule } from '@/store/upgrade/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import TryAQuestion from '@/components/Register/TryAQuestion/TryAQuestion.vue'
import PassWithConfidence from '@/components/Register/TryAQuestion/PassWithConfidence.vue'
import OneQuestionDown from '@/components/Register/TryAQuestion/OneQuestionDown.vue'
import { quizModule } from '@/store/quiz/module'
import { mobileModule } from '@/store/mobile/module'
import { toastModule } from '@/store/toast/module'
import { giftModule } from '@/store/gift/module'

const {
    ModalContainer,
    Link: PocketLink,
    Banner,
    Errors,
    Icon,
} = UIKit

const route = useRoute()
const router = useRouter()

const currentStep = ref(0)
const selectedExam = ref<null | TBundleExam>(null)
const selectedBundle = ref<null | Study.Class.BundleJSON | ClientBundle>(null)
const password = ref('')
const agreed = ref(false)
const selectedPlanId = ref<null | string>(null)
const searchVal = ref('')
const isLoading = ref(true)
const isLoadingPremiumActivation = ref(false)
const purchaseAttempted = ref(false)       // did the user attempt to purchase a plan?
const paymentFieldsRefreshTime = ref(Date.now())
const paymentError = ref('')
const premiumActivationError = ref('')
const isShowingPaymentSuccess = ref(false)
const isLoadingStep = ref(false)
const tryAQuestionVariantValue = ref<'try-a-question' | null>(null)
const tryAQuestionScreenName = ref<
    'pass_with_confidence' | 'try_a_question' | 'one_question_down' | 'account_details' | null
>(null)
/* Perceived Value Experiment */
const perceivedValueVariantValue = ref<'control' | 'header-treatment' | 'feature-list-treatment' | null>(null)
const hasTakenExam = ref<boolean | null>(null)
const version = ref(import.meta.env.VUE_APP_VERSION)
const examHasLevelUpMode = ref(false)
const firstNameQuery = ref<string | null>(null)
const lastNameQuery = ref<string | null>(null)
const emailQuery = ref<string | null>(null)

const currentRouteStep = computed(() => {
    if (currentStep.value === 0) {
        return 'onboarding'
    } else if (currentStep.value === 1 && !selectedBundle.value && !selectedExam.value) {
        return 'industry'
    } else if (currentStep.value === 1 && selectedBundle.value && !selectedExam.value) {
        return 'exam'
    } else if (currentStep.value === 1 && selectedBundle.value && selectedExam.value) {
        return 'exam_version'
    } else if (currentStep.value === 2) {
        return 'first_time_exam_ask'
    } else if (currentStep.value === 3) {
        // A/B Test Try A Question
        if (showTryAQuestion.value && tryAQuestionScreenName.value) {
            return tryAQuestionScreenName.value
        } else {
            return 'account_details'
        }
    } else if (currentStep.value === 4) {
        return 'plan'
    } else if (currentStep.value === 5) {
        return 'payment'
    }
    return ''
})

const examVersions = computed(() => {
    return ([ ...selectedExam.value?.bundle.exams.map(e =>
        examMetadataModule.getters.getExamMetadataById({ excludeDiscontinued: true })[e.objectId]
    ) || [] ])
        .filter(e => e && e.examGuid === selectedExam.value?.examGuid)
        .sort((a, b) => -a.version.localeCompare(b.version, undefined, { numeric: true }))
})

const validReferral = computed(() => {
    return referralModule.getters.getValidReferral()
})

const referralError = computed(() => {
    return referralModule.getters.getReferralError()
})

const referralId = computed(() => {
    return !validReferral.value
        ? ''
        : 'objectId' in validReferral.value
            ? validReferral.value.objectId
            : validReferral.value.id
})

const stripePricesForBundle = computed(() => {
    return selectedBundle.value
        ? stripeModule.getters.getStripePricesForBundle(selectedBundle.value.objectId)
        : []
})

const selectedPlan = computed(() => {
    return stripePricesForBundle.value.find(price => price.id === selectedPlanId.value)
})

const planInfo = computed(() => {
    return {
        intervalCount: selectedPlan.value?.recurring ? selectedPlan.value.recurring.interval_count : 1,
        interval: selectedPlan.value?.recurring?.interval || 'month',
        amount: selectedPlan.value?.unit_amount,
    }
})

const subscriptionPlanLength = computed(() => {
    return subscriptionModule.getters.getSubscriptionPlanLengthByExamId(selectedExam.value?.objectId)
})

const isOOAP = computed(() => {
    return mobileModule.getters.isInArrivedFromMobileState()
})

/* Start Marketing Scope Onboarding  */
const isMarketingCampaign = computed(() => {
    return typeof route.query.utm_campaign === 'string'
        && route.query.utm_campaign === 'one_message'
})

const showMarketingExamScreenUpdates = computed(() => {
    return typeof route.query.utm_campaign === 'string'
        && route.query.utm_campaign === 'one_message'
        && typeof route.query.bundleId === 'string'
})
/* End Marketing Scope Onboarding  */


/* Start Try A Question Experiment Getters  */
const showingTryAQuestionNext = computed(() => {
    return currentStep.value === 2
        && selectedExam.value?.nativeAppName === 'NREMT® EMT'
        && tryAQuestionVariantValue.value === 'try-a-question'
})

const showTryAQuestion = computed(() => {
    // show if it is step 3, the selected exam is EMT and the variant is 'try-a-question'
    return currentStep.value === 3
        && selectedExam.value?.nativeAppName === 'NREMT® EMT'
        && tryAQuestionVariantValue.value === 'try-a-question'
})

const passWithConfidenceSubHeader = computed(() => {
    if (selectedExam.value?.nativeAppName) {
        return hasTakenExam.value
            ? `You have taken the ${selectedExam.value?.nativeAppName} before.`
            : `You haven\'t taken the ${selectedExam.value?.nativeAppName} before.`
    }
    return hasTakenExam.value
        ? 'You have taken this exam before.'
        : 'You haven\'t taken this exam before.'
})
/* End Try A Question Experiment Getters */

const referralMetadataBannerMessage = computed(() => {
    return referralModule.getters.getMetadataBannerMessage()
})

const referralMetadataPreselectedPlan = computed(() => {
    return referralModule.getters.getMetadataPreselectedPlan()
})

// Gifting Computeds
const validGift = computed(() => {
    return giftModule.getters.getValidGift()
})

const giftError = computed(() => {
    return giftModule.getters.getGiftError()
})

const giftTier = computed(() => {
    return giftModule.getters.getGiftTier()
})

const giftTierDuration = computed(() => {
    return giftTier.value === 'gift-3-month'
        ? '3 months'
        : '1 month'
})

const crossPlatformConfig = computed(() => {
    return userModule.getters.getCrossPlatformConfig()
})

const isAnAlreadyRegisteredUser = computed(() => {
    return userModule.state.user?.webConfig?.hasSeenWelcomeModal
        || crossPlatformConfig.value?.levelUpFreeTrial?.hasSeenLevelUpTrialSuggestion
        || userModule.state.user?.webConfig?.hasSeenProductAnnouncementModal
})

const isInviteLicenseFlow = computed(() => {
    return route.fullPath.includes('activateLicense')
        || typeof route.query.license === 'string'
        || typeof route.query.groupCode === 'string'
})

onMounted(async () => {
    await Promise.all([
        userModule.actions.fetchUserData(),
        stripeModule.actions.fetchStripePrices(),
        referralModule.actions.validateReferral(),
        giftModule.actions.validateGift(),
    ])

    if (isAnAlreadyRegisteredUser.value) {
        await router.push({ name: 'study', query: route.query })
        return
    }

    if (isInviteLicenseFlow.value) {
        await router.push({
            name: 'invite',
            query: route.query,
        })
        return
    }

    if (typeof route.query.exam === 'string') {
        await Promise.all([
            examMetadataModule.actions.fetchExamMetadata(),
            bundleModule.actions.fetchBundles(),
        ])

        const examMetadata = examMetadataModule.getters.getMostRecentExamForExamGuid(route.query.exam)
        const bundle = bundleModule.getters.getBundles()
            .find(b => b.exams.find(e => e.objectId === examMetadata?.objectId))

        selectedExam.value = bundle
            ? {
                ...examMetadata,
                bundle,
            }
            : null
        selectedBundle.value = bundle || null
        if (selectedBundle.value && selectedExam.value) {
            await moveToConfirmExam()
        }
    } else if (typeof route.query.bundleId === 'string') {
        // Allow ChooseExam to process bundleId query param
        moveToChooseExam()
    }

    updateRouteStep()
    focusTitle()
    isLoading.value = false
})

const focusTitle = () => {
    setTimeout(() => {
        const titleEl = document.querySelector('.register__title') as HTMLElement
        titleEl?.focus()
    }, 1)
}

const scrollTop = () => {
    const registerEl = document.querySelector('.register')
    registerEl?.scrollTo(0, 0)
}

const search = (searchValParam: string) => {
    searchVal.value = searchValParam
}

// Try A Question Experiment Fetch Variant
const fetchTryAQuestionVariant = async () => {
    // Only fetch the variant once
    if (isLoadingStep.value || tryAQuestionVariantValue.value) {
        return
    }

    isLoadingStep.value = true

    const tryAQuestionVariant = await analyticsModule.actions.amplitudeFetchVariant('try-a-question')
    const tryAQuestionVariantValueRes = tryAQuestionVariant?.value as (
        'try-a-question' | 'control' | undefined
    )

    tryAQuestionVariantValue.value = tryAQuestionVariantValueRes === 'try-a-question' ? 'try-a-question' : null
    tryAQuestionScreenName.value = tryAQuestionVariantValueRes ? 'pass_with_confidence' : null

    isLoadingStep.value = false
}

// Perceived Value Experiment Fetch Variant
const fetchPerceivedValueVariant = async () => {
    // Only fetch the variant once, and never for users who arrived from the mobile app to purchase
    if (isLoadingStep.value || perceivedValueVariantValue.value || isOOAP.value) {
        return
    }

    isLoadingStep.value = true

    const perceivedValueVariant = await analyticsModule.actions.amplitudeFetchVariant('perceived-value-experiment')
    const perceivedValueVariantValueRes = perceivedValueVariant?.value as (
        'control' | 'header-treatment' | 'feature-list-treatment' | undefined
    )

    const isValidVariant = perceivedValueVariantValueRes && [
        'header-treatment',
        'feature-list-treatment',
        'control',
    ].includes(perceivedValueVariantValueRes)

    perceivedValueVariantValue.value = isValidVariant
        ? perceivedValueVariantValueRes
        : 'control'

    isLoadingStep.value = false
}

const moveToChooseExam = () => {
    currentStep.value = 1
    updateRouteStep()
}

const selectBundle = (bundle: Study.Class.BundleJSON | ClientBundle) => {
    selectedBundle.value = bundle
    updateRouteStep()
    scrollTop()
}

const selectExam = (exam: TBundleExam) => {
    selectedExam.value = exam
    if (examVersions.value.length <= 1) {
        moveToConfirmExam()
    } else {
        updateRouteStep()
    }
}

const selectExamVersion = (exam: Study.Class.ExamMetadataJSON | ClientExam) => {
    if (!selectedExam.value?.bundle || !exam) {
        return
    }

    selectedExam.value = {
        ...exam,
        bundle: selectedExam.value?.bundle,
    }
}

const moveToConfirmExam = async () => {
    // A/B Try A Question Experiment only need to check for this variant if the selected exam is EMT
    if (selectedExam.value?.nativeAppName === 'NREMT® EMT') {
        await fetchTryAQuestionVariant()
    }

    currentStep.value = 2
    updateRouteStep()
}

const updateRouteStep = () => {
    const stepName = currentRouteStep.value
    if (stepName && route.query.step !== stepName) {
        router.replace({
            name: 'register',
            query: {
                ...route.query,
                step: stepName,
            },
        })
    }

    focusTitle()
}

const goBack = () => {
    analyticsModule.actions.amplitudeTrack('Button Clicked', {
        callToAction: 'Back',
        screenName: analyticsModule.getters.getScreenName(route),
    })

    if (currentStep.value === 2 || (currentStep.value === 1 && selectedExam.value)) {
        backToExamSearch()
    } else if (showTryAQuestion.value && tryAQuestionScreenName.value) {
        // A/B Try A Question Experiment
        // if screen is pass_with_confidence, take the user back to first time exam ask
        if (tryAQuestionScreenName.value === 'pass_with_confidence') {
            const newStep = currentStep.value - 1
            currentStep.value = Math.max(newStep, 1)
            updateRouteStep()
            focusTitle()
        } else {
            previousTryAQuestionScreenName()
        }
    } else {
        const newStep = currentStep.value - 1
        currentStep.value = Math.max(newStep, 1)
        updateRouteStep()
        focusTitle()
    }
}

const backToExamSearch = () => {
    currentStep.value = 1
    selectedExam.value = null
    selectedBundle.value = null
    hasTakenExam.value = null
    scrollTop()
    updateRouteStep()
}

const updateHasTakenExam = (hasTakenExamParam: boolean | null) => {
    hasTakenExam.value = hasTakenExamParam
}

const submitFirstTimeExamAsk = () => {
    if (selectedBundle.value && selectedExam.value && hasTakenExam.value !== null) {
        analyticsModule.actions.amplitudeTrack('First Time Exam Ask Answered', {
            bundleId: selectedBundle.value.objectId,
            examGuid: selectedExam.value.examGuid,
            hasTakenExam: hasTakenExam.value,
        })
    }
    confirmExam()
}

const confirmExam = () => {
    currentStep.value = 3
    updateRouteStep()
    scrollTop()
}

const submitAccountDetails = async ({
    password: passwordParam,
    redeemGift: redeemGiftParam,
}: { password: string; redeemGift: boolean }) => {
    password.value = passwordParam
    agreed.value = true

    if (redeemGiftParam) {
        await redeemGift()
    } else if (selectedExam.value?.isFree) {
        confirmPlan('free')
    } else {
        // Perceived Value Experiment
        await fetchPerceivedValueVariant()
        currentStep.value = 4
        updateRouteStep()
    }

    if (selectedExam.value && hasTakenExam.value !== null) {
        try {
            await userExamMetadataModule.actions.upsertUserExamBackground({
                examMetadataId: selectedExam.value.objectId,
                hasTakenExam: hasTakenExam.value,
            })
        } catch (err) {
            // Soft fail to avoid blocking the user
            Sentry.captureException(err)
        }
    }

    scrollTop()
}

const getNumOfSubscriptionDays = () => {
    if (planInfo.value?.interval === 'month') {
        return planInfo.value.intervalCount * 30
    }

    if (planInfo.value?.interval === 'year') {
        return 365
    }

    return 0
}

const confirmPlan = async (planId: string | null) => {
    selectedPlanId.value = planId

    if (planId === 'free') {
        amplitudeUpgradeViewed()

        if (route.query.referral) {
            referralModule.actions.trackHasSeenReferralUpgradePanel()
        }

        await router.replace({
            name: 'study',
            query: {
                ...route.query,
                step: undefined,
                firstName: undefined,
                lastName: undefined,
                email: undefined,
                exam: undefined,
            },
        })
    } else {
        currentStep.value = 5
        purchaseAttempted.value = true
        updateRouteStep()
        scrollTop()
    }
}

const redeemGift = async () => {
    if (isLoadingPremiumActivation.value) {
        return
    }

    isLoadingPremiumActivation.value = true

    try {
        await giftModule.actions.activateGift()
        await giftModule.actions.wipeGiftContext()
        await userModule.actions.updateWebConfig({ hasSeenWelcomeModal: true })

        amplitudeOnboardingCompleted()
        isShowingPaymentSuccess.value = true
        currentStep.value = 5
        toastModule.actions.displayToast({ title: 'Gift Redeemed!' })
    } catch (err) {
        Sentry.captureException(err)
        premiumActivationError.value =
            'Unable to redeem gift. Close the window and try your link again. If the error persists, contact support.'
    }

    isLoadingPremiumActivation.value = false
}

const checkoutCompleted = async (checkoutSessionId: string) => {
    try {
        const checkoutSession = await stripeModule.actions.fetchCheckoutSession({
            checkoutSessionId: checkoutSessionId,
        })

        if (checkoutSession.status !== 'complete') {
            Sentry.captureException(
                new Error(`Incomplete checkout status for checkout session ${checkoutSessionId}`)
            )
            paymentFieldsRefreshTime.value = Date.now()
            paymentError.value = 'Your payment failed or was canceled. Please try again.'
            nextTick(() => {
                scrollTop()
            })
            return
        }

        isLoadingPremiumActivation.value = true
        isShowingPaymentSuccess.value = true

        const subscriptionId = checkoutSession.subscription as string | null

        if (!subscriptionId) {
            throw new Error(`Missing subscription from checkout session ${checkoutSessionId}: `)
        }

        const paymentPlatform = upgradeModule.getters.getPaymentPlatform()

        await subscriptionModule.actions.activateSubscription({
            subscriptionId: subscriptionId,
            paymentPlatform: paymentPlatform,
        })

        await userModule.actions.updateWebConfig({
            hasSeenWelcomeModal: true,
        })

        amplitudeUpgradeViewed()

        await analyticsModule.actions.trackEvent('Upgrade_Viewed', {
            result: 'Subscribed',
            ...(selectedExam.value && { exam: selectedExam.value.nativeAppName }),
        })

        if (!isOOAP.value) {
            amplitudeOnboardingCompleted()
        }
    } catch (err) {
        Sentry.captureException(err)
        premiumActivationError.value =
            'There was an error updating your account. Try refreshing your page or contacting support.'
        await userModule.actions.fetchUserData({ stripeSubs: true })
    }
    isLoadingPremiumActivation.value = false
}

const handlePaymentFieldsError = async () => {
    paymentError.value = 'Error loading payment fields. Try refreshing your page or contacting support.'
    await userModule.actions.fetchUserData({ stripeSubs: true })
}

const registrationCompleted = async () => {
    await router.replace({ name: 'study' })
}

const amplitudeUpgradeViewed = () => {
    const didSubscribe = !!subscriptionModule.getters.getSubscriptionForExamId()
    const numOfSubscriptionDays = didSubscribe ? getNumOfSubscriptionDays() : 0

    analyticsModule.actions.amplitudeTrack('Upgrade Viewed', {
        bundleId: selectedBundle.value?.objectId,
        client: 'Web Study',
        clientAppId: 'study.pocketprep.com',
        clientVersion: version.value,
        examGuid: selectedExam.value?.examGuid,
        userDomain: userModule.getters.getUserDomain() || undefined,
        upgradeDidSubscribe: didSubscribe,
        upgradePurchasePressed: purchaseAttempted.value,
        upgradePriorScreen: 'Onboarding',
        upgradeSubscriptionDays: numOfSubscriptionDays || 0,
    })
}

const amplitudeOnboardingCompleted = async () => {
    const bundleId = selectedExam.value?.bundle.objectId

    if (
        selectedExam.value?.examGuid
        && bundleId
        && subscriptionPlanLength.value
    ) {
        await userModule.actions.onboardingCompleted({
            firstExamGuid: selectedExam.value?.examGuid,
            firstBundleId: bundleId,
            signedUpOn: 'Web',
            onboardingResult: subscriptionPlanLength.value,
        })
    }
}

watch(selectedExam, async () => {
    if (selectedExam.value?.examGuid) {
        examHasLevelUpMode.value = await quizModule.actions.isLevelUpEnabled({
            examGuid: selectedExam.value?.examGuid,
        })
    }
})

watch(() => route.query, () => {
    if (typeof route.query.firstName === 'string') {
        firstNameQuery.value = route.query.firstName
    }

    if (typeof route.query.lastName === 'string') {
        lastNameQuery.value = route.query.lastName
    }

    if (typeof route.query.email === 'string') {
        emailQuery.value = route.query.email
    }
}, { immediate: true })

/* Start Try A Question Experiment Methods  */
const previousTryAQuestionScreenName = () => {
    if (tryAQuestionScreenName.value === 'try_a_question') {
        tryAQuestionScreenName.value = 'pass_with_confidence'
    } else if (tryAQuestionScreenName.value === 'one_question_down') {
        tryAQuestionScreenName.value = 'try_a_question'
    } else {
        tryAQuestionScreenName.value = 'one_question_down'
    }

    updateRouteStep()
}

const tryAQuestion = () => {
    tryAQuestionScreenName.value = 'try_a_question'
    updateRouteStep()
}

const seeStats = (answeredCorrectly: boolean) => {
    tryAQuestionScreenName.value = 'one_question_down'
    analyticsModule.actions.amplitudeTrack('Try a Question Answer Submitted', {
        result: answeredCorrectly ? 'correct' : 'incorrect',
    })
    updateRouteStep()
}

const createAccount = () => {
    tryAQuestionScreenName.value = 'account_details'
    updateRouteStep()
}
/* End Try A Question Experiment Methods */
</script>
<style lang="scss" scoped>
.register {
    width: 1010px;
    background: $gray-background;
    height: calc(80vh - 56px);
    min-height: 640px;
    box-sizing: border-box;
    overflow: auto;
    border-radius: 0 0 12px 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;

    @include breakpoint(brown-bear) {
        width: 740px;
        height: calc(100vh - 140px) !important;
    }

    @include breakpoint(black-bear) {
        max-height: none;
        height: calc(100vh - 56px) !important;
        width: 100%;
        min-height: 0;
    }

    &--payment-success {
        overflow: hidden;

        @include breakpoint(black-bear) {
            height: 100% !important;
        }
    }

    &__banner,
    &__gift-banner {
        max-width: 600px;
        height: auto;
        margin-top: 24px;

        &--referral-metadata-banner-message {
            max-width: 610px;
        }

        @include breakpoint(black-bear) {
            max-width: 350px;
        }
    }

    &__gift-banner {
        :deep(.uikit-banner__content) {
            display: flex;
            align-items: center;
        }
    }

    &__banner-gift-emoji {
        width: 16px;
        height: 22px;
        margin-left: 14px;
    }

    &__error-banner {
        margin-top: 24px;
        max-width: 550px;

        @include breakpoint(black-bear) {
            margin-top: 16px;
            max-width: 300px;
        }
    }

    &__choose-exam {
        @include breakpoint(black-bear) {
            margin-bottom: 42px;
        }
    }

    &__existing-account {
        display: none;

        @include breakpoint(brown-bear) {
            display: block;
            margin: 12px 0 5px;
            text-align: center;
        }
    }

    &__step {
        font-size: 13px;
        line-height: 18px;
        text-align: center;
        color: $slate-03;
        padding-top: 36px;
        margin-bottom: 10px;

        &--referral,
        &--gift {
            padding-top: 40px;
        }

        @include breakpoint (black-bear) {
            padding-top: 21px;
        }
    }

    &__title {
        font-size: 26px;
        outline: none;
        line-height: 36px;
        font-weight: 700;
        text-align: center;
        margin: 0;
        padding: 0 25px;

        &--payment {
            margin-top: 42px;

            @include breakpoint(black-bear) {
                margin-top: 24px;
            }
        }

        &--marketing-campaign-first-time {
            max-width: 450px;
        }

        @include breakpoint(black-bear) {
            max-width: 323px;
        }
    }

    &__subtitle {
        color: #475967;
        font-size: 14px;
        line-height: 19px;
        text-align: center;
        margin-bottom: 36px;
        margin-top: 4px;

        @include breakpoint(black-bear) {
            margin-bottom: 4px;
        }

        &--error {
            color: $pepper;
        }
    }

    &__payment-success {
        position: relative;
        width: 100%;
        height: 100%;
        background-color: $floral-white;
        padding-top: 78px;
        padding-bottom: 32px;

        @include breakpoint(black-bear) {
            padding-top: 42px;
        }
    }

    &__financial-support {
        display: flex;
        width: 100%;
        align-items: center;
        align-content: center;
        padding-top: 14px;
        padding-bottom: 14px;
        color: $slate;
        font-size: 13px;
        font-weight: 500;
        line-height: 18px;
        border-radius: 0px 0px 12px 12px;

        @include breakpoint(black-bear) {
            height: 66px;
        }
    }

    &__financial-support-message {
        margin-left: 80px;

        @include breakpoint(black-bear) {
            margin-left: 27px;
            margin-right: 28px;
        }
    }

    &__financial-support-link {
        font-size: 13px;
        font-style: normal;
        font-weight: 500;
        line-height: 18px;

        svg {
            margin-left: 3px;
            width: 9px;
            height: 10px;
        }
    }
}
</style>
